import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { SessionStats } from "@/features/sessions";
import { useChartTheme } from "@/features/charts/theme";
import { ChartTooltip } from "@/features/charts/tooltip";
import { resultTone } from "@/features/sessions/result";
import { ResponsivePie } from "@nivo/pie";
import { useEffect, useRef } from "react";
import { bySliceSize, commonPieConfig } from "./utils";
import { PieLegend } from "./pie-legend";
import { useTranslation } from "@/features/utils/i18n";

interface PerformanceProps {
  stats: SessionStats | undefined;
}

/**
 * The sentiment bar's three segments, on the status tokens. Neutral is a paler
 * green, not the warning orange: a conversation nobody was unhappy about is not
 * a warning, and orange beside red read as two shades of trouble. Its share is
 * written in dark green, white being unreadable on the paler fill.
 */
const SENTIMENTS = [
  { id: "positive", className: "bg-success", text: "text-white" },
  {
    id: "neutral",
    className: "bg-success/40",
    text: "text-success-foreground",
  },
  { id: "negative", className: "bg-danger", text: "text-white" },
] as const;

export function Performance({ stats }: PerformanceProps) {
  const { t } = useTranslation("dashboard");
  const { theme, tokens } = useChartTheme();
  const isFirstRender = useRef(true);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  if (!stats) return null;

  const getResultLabel = (result: string): string => {
    const key = `performance.results.${result}` as const;
    const translated = t(key);
    // If translation key is returned as-is, use result as-is
    return translated === key ? result : translated;
  };

  // Prepare data for results pie chart
  // Each outcome in the tone the calls list draws its badge in, so a green
  // slice here is the green "Succès" there.
  const resultsData = stats.sessions_by_result
    .map((resultData) => ({
      id: resultData.result,
      label: getResultLabel(resultData.result),
      value: resultData.count,
      color: tokens.tone(resultTone(resultData.result)),
    }))
    .sort(bySliceSize);

  const sentimentCount = (sentiment: string) =>
    stats.sessions_by_sentiment.find((s) => s.sentiment === sentiment)
      ?.count || 0;
  const totalSessions = stats.sessions_by_sentiment.reduce(
    (sum, s) => sum + s.count,
    0,
  );
  const sentimentShare = (sentiment: string) =>
    totalSessions > 0 ? (sentimentCount(sentiment) / totalSessions) * 100 : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("performance.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {resultsData.length > 0 ? (
          <div className="space-y-4">
            <div className="h-[200px]">
              <ResponsivePie
                {...commonPieConfig}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                enableArcLinkLabels={false}
                data={resultsData}
                theme={theme}
                animate={isFirstRender.current}
                motionConfig="gentle"
                tooltip={({ datum }) => (
                  <ChartTooltip
                    title={String(datum.label)}
                    rows={[
                      {
                        color: datum.color,
                        label: t("performance.count"),
                        value: datum.value,
                      },
                    ]}
                  />
                )}
              />
            </div>
            <PieLegend
              data={resultsData}
              total={resultsData.reduce((sum, d) => sum + d.value, 0)}
            />
          </div>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
            {t("performance.noData")}
          </div>
        )}

        {/* Sentiment Gauge */}
        <div>
          <div className="space-y-2 max-w-sm mx-auto">
            <div className="flex justify-between text-xs text-muted-foreground">
              {SENTIMENTS.map(({ id, className }) => (
                <div key={id} className="flex items-center gap-1">
                  <div className={`size-3 rounded-xs ${className}`} />
                  <span>
                    {t(`performance.sentiment.${id}`)} ({sentimentCount(id)})
                  </span>
                </div>
              ))}
            </div>
            <div className="flex h-4 w-full overflow-hidden rounded-full bg-muted">
              {SENTIMENTS.map(({ id, className, text }) => {
                const share = sentimentShare(id);
                return share > 0 ? (
                  <div
                    key={id}
                    className={`${className} ${text} flex items-center justify-center text-xs font-medium`}
                    style={{ width: `${share}%` }}
                  >
                    {share >= 10 && `${Math.round(share)}%`}
                  </div>
                ) : null;
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
